<script setup lang="ts">
import { $getRoot, $parseSerializedNode, type RangeSelection, type SerializedEditorState } from "lexical";
import { useLexicalComposer } from "lexical-vue";
import { computed, onMounted } from "vue";

import { $createDartLinkNode } from "~/components/text/nodes/DartLinkNode";
import { $createEntityNode, EntityNode } from "~/components/text/nodes/EntityNode";
import { insertNodeAtSelection } from "~/components/text/utils";
import { getDartboardUrl, getDocUrl, getFolderUrl, getSkillUrl, getTaskUrl, getViewUrl } from "~/router/common";
import { EntityName, TypeaheadSpecialEntity } from "~/shared/enums";
import type { TypeaheadOption } from "~/shared/types";
import { useAppStore, useDataStore } from "~/stores";

import { ADD_ATTACHMENT_OPTION_VALUE, createChatContextOptionFns, getSkillOptionsSync } from "./links";
import TypeaheadPlugin from "./TypeaheadPlugin.vue";

const props = defineProps<{
  isChat?: boolean;
  hideAttachmentOption?: boolean;
}>();

const emit = defineEmits<{
  insertAttachmentDuids: [attachmentDuids: string[]];
  openAttachmentInput: [];
}>();

const appStore = useAppStore();
const dataStore = useDataStore();
const editor = useLexicalComposer();

const insertSerializedState = (state: SerializedEditorState, selection?: RangeSelection) => {
  editor.update(() => {
    const root = $getRoot();
    state.root.children.forEach((child) => {
      insertNodeAtSelection($parseSerializedNode(child), selection ?? root.selectEnd());
    });
  });
};

const typeahead = computed(() => ({
  trigger: "@",
  getOptions: createChatContextOptionFns(appStore.currentPage, {
    includeSkills: props.isChat,
    includeUsers: props.isChat,
    hideAttachmentOption: props.hideAttachmentOption,
  }),
}));

const skillTypeahead = computed(() => ({
  trigger: "$",
  getOptions: [async () => getSkillOptionsSync()],
}));

const onSelect = async (selection: RangeSelection, option: TypeaheadOption) => {
  if (option.value === ADD_ATTACHMENT_OPTION_VALUE) {
    emit("openAttachmentInput");
    return;
  }
  if (typeof option.value === "string" && option.specialEntity) {
    switch (option.specialEntity) {
      case TypeaheadSpecialEntity.TASK: {
        const task = await dataStore.getTaskByDuid(option.value);
        if (!task) {
          return;
        }
        editor.update(() => {
          insertNodeAtSelection($createDartLinkNode(getTaskUrl(task), EntityName.TASK, task.duid), selection);
        });
        break;
      }
      case TypeaheadSpecialEntity.DOC: {
        const doc = await dataStore.getDocByDuid(option.value);
        if (!doc) {
          return;
        }
        editor.update(() => {
          insertNodeAtSelection($createDartLinkNode(getDocUrl(doc), EntityName.DOC, doc.duid), selection);
        });
        break;
      }
      case TypeaheadSpecialEntity.VIEW: {
        const view = dataStore.getViewByDuid(option.value);
        if (!view) {
          return;
        }
        editor.update(() => {
          insertNodeAtSelection($createDartLinkNode(getViewUrl(view), EntityName.VIEW, view.duid), selection);
        });
        break;
      }
      case TypeaheadSpecialEntity.DARTBOARD: {
        const dartboard = dataStore.getDartboardByDuid(option.value);
        const space = dataStore.getSpaceByDuid(dartboard?.spaceDuid ?? "");
        if (!dartboard || !space) {
          return;
        }
        editor.update(() => {
          insertNodeAtSelection(
            $createDartLinkNode(getDartboardUrl(dartboard, space), EntityName.DARTBOARD, dartboard.duid),
            selection
          );
        });
        break;
      }
      case TypeaheadSpecialEntity.FOLDER: {
        const folder = dataStore.getFolderByDuid(option.value);
        const space = dataStore.getSpaceByDuid(folder?.spaceDuid ?? "");
        if (!folder || !space) {
          return;
        }
        editor.update(() => {
          insertNodeAtSelection($createDartLinkNode(getFolderUrl(folder), EntityName.FOLDER, folder.duid), selection);
        });
        break;
      }
      case TypeaheadSpecialEntity.SKILL: {
        const skill = dataStore.getSkillByDuid(option.value);
        if (!skill) {
          return;
        }
        editor.update(() => {
          insertNodeAtSelection($createDartLinkNode(getSkillUrl(skill), EntityName.SKILL, skill.duid), selection);
        });
        if (skill.attachmentDuids.length > 0) {
          emit("insertAttachmentDuids", skill.attachmentDuids);
        }
        break;
      }
      default: {
        break;
      }
    }
  }

  if (!option.property) {
    return;
  }

  const entityNode = $createEntityNode(option.property.duid, option.value);
  insertNodeAtSelection(entityNode, selection);
};

const insertSkillLinkNode = (skillDuid: string) => {
  editor.update(() => {
    const skill = dataStore.getSkillByDuid(skillDuid);
    if (!skill) {
      return;
    }
    const root = $getRoot();
    insertNodeAtSelection($createDartLinkNode(getSkillUrl(skill), EntityName.SKILL, skill.duid), root.selectEnd());
  });
};

defineExpose({
  insertSerializedState,
  insertSkillLinkNode,
});

onMounted(() => {
  if (!editor.hasNodes([EntityNode])) {
    throw new Error("AiChatTypeaheadPlugin: EntityNode not registered on editor");
  }
});
</script>

<template>
  <TypeaheadPlugin
    :key="typeahead.trigger"
    :trigger="typeahead.trigger"
    :get-options-fns="typeahead.getOptions"
    @select="onSelect" />
  <TypeaheadPlugin
    v-if="isChat"
    :key="skillTypeahead.trigger"
    :trigger="skillTypeahead.trigger"
    :get-options-fns="skillTypeahead.getOptions"
    @select="onSelect" />
</template>
